import { TValidationRules } from '@/main/store/@types/page';
import axios, { AxiosResponse } from 'axios';
import _ from 'lodash';
import { editStoreBuilder } from '@/main/store/pages/edit/builder';
import { updateEditAction } from '@/main/store/actionsFunctions';
import { valueMapper } from '@/main/utils/dataMapper';
import { ERROR, START_LOADING_V2, END_LOADING_V2 } from '@/main/store/listEditTypes';

import { editActions, editMutations, Pneu, PneuPart } from './types';
import { NAME as name, CRUMB_LABEL_EDIT } from '../constants';
import { apiProvider } from '../api';

const edit = {
	ZakaznikChecked: true as boolean | null,
	VozidloChecked: null as boolean | null,
	ZaznamId: null as number | null,
	SkladpneuOzn: null as number | null,
	SkladmistopId: null as number | null,
	SkladmistopIdPopis: null as string | null,
	SkladmistopIdText: null as string | null,
	VozidloId: null as number | null,
	VozidloIdText: null as string | null,
	ZakaznikId: null as number | null,
	ZakaznikIdText: null as string | null,
	PracovnikId: null as number | null,
	PracovnikIdText: null as string | null,
	PracovnikOsobniCis: null as string | null,
	ProvozovnaId: null as number | null,
	ProvozovnaIdText: null as string | null,
	StrednaklOzn: null as number | null,
	StrednaklOznText: null as string | null,
	Text: null as string | null,
	Pc: null as number | null,
	Poznamka: null as string | null,
	VstupDat: null as Date | null,
	VstupPracId: null as number | null,
	VstupPracIdText: null as string | null,
	EditDat: null as Date | null,
	EditPracId: null as number | null,
	EditPracIdText: null as string | null,
	DphSzbOzn: null as number | null,
	DokladPfx: null as string | null,
	DokladNr: null as number | null,
	TiskDat: null as Date | null,
	ZodpovosobaId: null as number | null,
	ZodpovosobaIdText: null as string | null,
	ZakazkaId: null as number | null,
	ZakazkaIdNr: null as number | null,
	ZakazkaIdText: null as string | null,
	Oznam: null as string | null,
	UserMessageList: [] as object[],
	PneuskladPolozky: [] as number[],
	reEditMode: false as boolean,
};

export const pnue: Pneu = {
	ZaznamId: null,
	PneuskladId: null,
	PneuPrp: true,
	VyrobekpneuId: null,
	PneuText: null,
	PneuPzn: null,
	PneusirkaId: null,
	PneuvyskaId: null,
	PneurozmerId: null,
	PrychlostPzn: null,
	PnosnostPzn: null,
	DezenHloubka: null,
	PstavPzn: null,
	DiskPrp: true,
	VyrobekdiskId: null,
	DiskText: null,
	DisksirkaId: null,
	DiskrozmerId: null,
	DstavPzn: null,
	KrytPrp: null,
	SroubPocet: null,
	PneuPoznamka: null,
	DiskPoznamka: null,
	VstupDat: null,
	VstupPracId: null,
	EditDat: null,
	EditPracId: null,
	PkonstrPzn: null,
	DiskPzn: null,
	PneumontPzn: null,
	ZakazkaId: null,
	Et: null,
	Prumstrotv: null,
	Roztecsrou: null,
	VyrobaRok: null,
	ValidationRules: {},
	DropDownList: {},
};

function setDiskValidationRules(rules: TValidationRules, required: boolean) {
	rules.VyrobekdiskId.required = required;
	rules.DiskPoznamka.required = required;
	rules.DiskrozmerId.required = required;
	rules.DisksirkaId.required = required;
	rules.DstavPzn.required = required;
	rules.DiskPzn.required = required;
}
function setPneValidationRules(rules: TValidationRules, required: boolean) {
	rules.VyrobekpneuId.required = required;
	rules.PneuPoznamka.required = required;
	rules.PneusirkaId.required = required;
	rules.PneuvyskaId.required = required;
	rules.PneurozmerId.required = required;
	rules.PnosnostPzn.required = required;
	rules.PrychlostPzn.required = required;
	rules.PkonstrPzn.required = required;
	rules.PneuPzn.required = required;
	rules.DezenHloubka.required = required;
}
function updatePolozkyValidationRules(
	pneus: Pneu[],
	{ index, pneuPart }: { index?: number, pneuPart?: PneuPart },
) {
	if (!_.isUndefined(index)) {
		const pneu = pneus[index];
		(pneuPart === PneuPart.Disk) && setDiskValidationRules(pneu.ValidationRules, pneu.DiskPrp);
		(pneuPart === PneuPart.Pneu) && setPneValidationRules(pneu.ValidationRules, pneu.PneuPrp);
	}

	pneus.forEach((pneu) => {
		setDiskValidationRules(pneu.ValidationRules, pneu.DiskPrp);
		setPneValidationRules(pneu.ValidationRules, pneu.PneuPrp);
	});
}

export const store = editStoreBuilder({
	name,
	apiProvider,
	crumbLabel: CRUMB_LABEL_EDIT,
	dropdownList: {
		DphSzbOzn: [],
		SkladpneuOzn: [],
	},
	edit,
	localState: {
		DphSazba: null as number | null,
		zakazkaDruh: null as string | null,
		disabledAll: false as boolean,
		DmstextId: null as number | null,
		polozky: {
			data: [] as Pneu[],
			areSame: true as boolean,
			default: null as Pneu | null,
			loadingCount: 0,
		},
	},
	depTableNames: [],
	depTableSettings: {},
	extraActions: {
		[editActions.LOAD_POLOZKY_DATA]: async ({ commit, state, dispatch }) => {
			try {
				commit(START_LOADING_V2, 'localState.polozky');
				const promises = state.edit.PneuskladPolozky.map((id) => apiProvider.getPolozka(id));
				const responses = await Promise.all(promises);
				responses.forEach((resp) => { commit(editMutations.SET_POLOZKA, resp.data); });
				return responses;
			} catch (err) {
				if (axios.isCancel(err)) return null;

				dispatch(ERROR, { objectName: state.name, err }, { root: true });
				return err;
			} finally {
				commit(END_LOADING_V2, 'localState.polozky');
			}
		},
		[editActions.CREATE_ALL_PNEU]: async ({ commit, state, dispatch }) => {
			const promiseArray = [] as Promise<AxiosResponse>[];
			state.localState.polozky.data.forEach((pneu) => {
				promiseArray.push(apiProvider.createPolozka(pneu));
			});
			await Promise.all(promiseArray);
		},
		[editActions.SAVE_ALL_PNEU]: async ({ commit, state, dispatch }) => {
			const promiseArray = [] as Promise<AxiosResponse>[];
			state.localState.polozky.data.forEach((pneu) => {
				promiseArray.push(apiProvider.savePolozka(pneu.ZaznamId!, pneu));
			});
			await Promise.all(promiseArray);
		},
		[editActions.PNEU_DEFAULT]: async ({ commit, state, dispatch }) => {
			try {
				commit(START_LOADING_V2, 'localState.polozky');
				const response = await apiProvider.getPneuDefault();
				commit(editMutations.SET_POLOZKA_DEFAULT, _.mapValues(response.data, valueMapper));
				return response;
			} catch (err) {
				if (axios.isCancel(err)) return null;

				dispatch(ERROR, { objectName: state.name, err }, { root: true });
				return err;
			} finally {
				commit(END_LOADING_V2, 'localState.polozky');
			}
		},
		[editActions.SKLAD_PNEU_SELECT_ZMENA]: ({ commit, state, dispatch }) => updateEditAction(
			state,
			commit,
			dispatch,
			apiProvider.zmenaSkladPneu(state.edit),
		),
		[editActions.SKLAD_MISTO_SELECT_ZMENA]: ({ commit, state, dispatch }) => updateEditAction(
			state,
			commit,
			dispatch,
			apiProvider.zmenaSkladMisto(state.edit),
		),
		[editActions.ZAKAZKA_SELECT_ZMENA]: ({ commit, state, dispatch }) => updateEditAction(
			state,
			commit,
			dispatch,
			apiProvider.zmenaZakazka(state.edit),
		),
		[editActions.VOZIDLO_SELECT_ZMENA]: ({ commit, state, dispatch }) => updateEditAction(
			state,
			commit,
			dispatch,
			apiProvider.zmenaVozidlo(state.edit),
		),
		[editActions.ZAKAZNIK_SELECT_ZMENA]: ({ commit, state, dispatch }) => updateEditAction(
			state,
			commit,
			dispatch,
			apiProvider.zmenaZakaznik(state.edit),
		),
		[editActions.PRACOVNIK_SELECT_ZMENA]: ({ commit, state, dispatch }) => updateEditAction(
			state,
			commit,
			dispatch,
			apiProvider.zmenaPracovnik(state.edit),
		),
	},
	extraGetters: {
		getPolozkaField: (state) => (path: string) => _.get(state.localState.polozky.data, path),
	},
	extraMutations: {
		updatePolozkaField(state, { path, value }: { path: string, value: any }) {
			if (state.localState.polozky.areSame) {
				const [, key] = path.split('].');
				state.localState.polozky.data.forEach((pneu: any) => { pneu[key] = value; });
			} else {
				_.set(state.localState.polozky.data, path, value);
			}
		},
		[editMutations.SET_POLOZKA](state, data) {
			const mapedData = _.mapValues(data, valueMapper) as Pneu;
			state.localState.polozky.data.push(mapedData);
			updatePolozkyValidationRules(state.localState.polozky.data, {});
		},
		[editMutations.SET_POLOZKA_DEFAULT](state, data) {
			state.localState.polozky.default = data as Pneu;
			updatePolozkyValidationRules([state.localState.polozky.default], {});
		},
		[editMutations.DECREASE_WHEEL](state) {
			if (state.localState.polozky.data.length > 0) {
				state.localState.polozky.data.pop();
			}
		},
		[editMutations.INCREASE_WHEEL](state) {
			const { polozky } = state.localState;
			const wheelToCopy = _.cloneDeep((polozky.data.length) ? _.first(polozky.data) : polozky.default);

			wheelToCopy!.PneumontPzn = polozky.data.length + 1;
			state.localState.polozky.data.push(wheelToCopy!);
		},
		[editMutations.UPDATE_POLOZKY_VALIDATION_RULES](
			state,
			{ index, pneuPart }: { index?: number, pneuPart: PneuPart },
		) {
			updatePolozkyValidationRules(state.localState.polozky.data, { index, pneuPart });
		},
		[editMutations.ZAKAZNIK_SELECT_MAPPER](state, { data }) {
			state.edit.ZakaznikId = data.value;
			state.edit.ZakaznikIdText = data.text;
		},
		[editMutations.SKLAD_MISTO_SELECT_MAPPER](state, { data }) {
			state.edit.SkladmistopId = data.id;
			state.edit.SkladmistopIdText = data.value;
			state.edit.SkladmistopIdPopis = data.text;
		},
		[editMutations.ZAKAZKA_SELECT_MAPPER](state, { data }) {
			state.edit.ZakazkaId = data.value;
			state.edit.ZakazkaIdText = data.text;
		},
		[editMutations.VOZIDLO_SELECT_MAPPER](state, { data }) {
			state.edit.VozidloId = data.value;
			state.edit.VozidloIdText = data.text;
		},
		[editMutations.VSTUP_ZAKAZNIK_SELECT_MAPPER](state, { data }) {
			state.edit.ZodpovosobaId = data.value;
			state.edit.ZodpovosobaIdText = data.text;
		},
		[editMutations.PRACOVNIK_SELECT_MAPPER](state, { data }) {
			state.edit.PracovnikId = data.value;
			state.edit.PracovnikIdText = data.jmeno || data.text ? `${data.text} ${data.jmeno}` : null;
			state.edit.PracovnikOsobniCis = data.osobniCis;
		},
		[editMutations.SKLAD_PNEU_TEXTY_SELECT_MAPPER](state, { data }) {
			state.localState.DmstextId = data.value;
			state.edit.Text = data.text;
		},
		[editMutations.NAKLAD_STREDISKO_SELECT_MAPPER](state, { data }) {
			state.edit.StrednaklOzn = data.value;
			state.edit.StrednaklOznText = data.text;
			if (!data.isClear) {
				state.edit.ProvozovnaId = data.provozovnaId;
			}
		},
	},
	props: {
		filter: {},
		mode: null,
	},
});
